import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Camera, Check, ImageOff, Shirt } from 'lucide-react'
import { formatDateTimeCompact } from '@/lib/dateTime'
import { cn } from '@/lib/utils'

export interface DetectionBBox {
  x: number
  y: number
  width: number
  height: number
}

/** One detection, as returned by getMergeSuggestionEvidence / listPersonDetections. */
export interface Detection {
  /** Identifies this detection — the unit a split moves and selects by. */
  eventId: string | null
  /**
   * The gallery entry this detection matched. Shared by every detection that
   * matched the same stored face, so it can never key selection or scoring.
   */
  faceId?: string | null
  timestamp: string | null
  deviceId?: string | null
  cameraName: string | null
  thumbnailUrl: string | null
  frameUrl: string | null
  bbox: DetectionBBox | null
  sightingId?: string | null
  qualityScore?: number | null
  /**
   * Best body/appearance agreement with another detection of the same person,
   * with the time gap to that match. Absent for detections captured before the
   * body vector was stored.
   */
  bodyMatch?: number | null
  bodyMatchGapMinutes?: number | null
}

export type ShotView = 'person' | 'frame'

/**
 * Body agreement below which a detection is flagged. Mirrors the ingest
 * matcher's `bodySimilarityThreshold` default — below this the appearance cue
 * is not considered evidence of the same person.
 */
const BODY_WEAK_MATCH = 0.45

/** Compact "how far apart" for the body-match tooltip. */
function formatGap(minutes: number | null): string {
  if (minutes == null) return '—'
  if (minutes < 1) return '<1m'
  if (minutes < 60) return `${Math.round(minutes)}m`
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)}h`
  return `${Math.round(minutes / (60 * 24))}d`
}

/**
 * Region of the frame to show in "person" view: the face bbox padded out to
 * include the torso (clothing and build are what settle most identity calls),
 * squared off to the card aspect and clamped inside the frame.
 */
export function computeCrop(
  bbox: DetectionBBox,
  frameW: number,
  frameH: number,
  targetAspect = 3 / 4
): { x: number; y: number; w: number; h: number } {
  const padX = bbox.width * 1.4
  const padTop = bbox.height * 1.0
  const padBottom = bbox.height * 3.0

  let x0 = bbox.x - padX
  let x1 = bbox.x + bbox.width + padX
  let y0 = bbox.y - padTop
  let y1 = bbox.y + bbox.height + padBottom

  // Grow the short side so the crop matches the card aspect (never shrink —
  // shrinking could cut the face back out of the box).
  if ((x1 - x0) / (y1 - y0) < targetAspect) {
    const width = (y1 - y0) * targetAspect
    const cx = (x0 + x1) / 2
    x0 = cx - width / 2
    x1 = cx + width / 2
  } else {
    const height = (x1 - x0) / targetAspect
    const cy = (y0 + y1) / 2
    y0 = cy - height / 2
    y1 = cy + height / 2
  }

  let w = x1 - x0
  let h = y1 - y0
  if (w >= frameW) {
    x0 = 0
    w = frameW
  } else {
    x0 = Math.min(Math.max(0, x0), frameW - w)
  }
  if (h >= frameH) {
    y0 = 0
    h = frameH
  } else {
    y0 = Math.min(Math.max(0, y0), frameH - h)
  }
  return { x: x0, y: y0, w, h }
}

interface DetectionShotProps {
  detection: Detection
  view: ShotView
  /** When provided the shot becomes a toggle button (split-person selection). */
  onToggle?: () => void
  selected?: boolean
  /** Plain click, with no selection checkbox. Ignored when onToggle is set. */
  onClick?: () => void
  /** Small overlay in the top-left, e.g. a similarity score. */
  badge?: ReactNode
}

/**
 * One detection: the source frame zoomed to the person (or the whole frame),
 * with the face boxed so it is obvious who to look at in a crowded scene.
 * Falls back to the stored face crop when the frame is gone (retention) — a
 * crop is still better evidence than an empty card.
 */
export function DetectionShot({
  detection,
  view,
  onToggle,
  selected = false,
  onClick,
  badge,
}: DetectionShotProps) {
  const { t } = useTranslation()
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null)
  const [failed, setFailed] = useState(false)

  const src = detection.frameUrl || detection.thumbnailUrl
  // A degenerate bbox would make the crop maths collapse — treat it as absent.
  const bbox = detection.bbox && detection.bbox.width > 0 && detection.bbox.height > 0
    ? detection.bbox
    : null
  const zoom = !!detection.frameUrl && !!bbox && view === 'person'

  useEffect(() => {
    setDims(null)
    setFailed(false)
  }, [src])

  const action = onToggle ?? onClick
  const Wrapper = action ? 'button' : 'div'
  const wrapperProps = action
    ? {
      type: 'button' as const,
      onClick: action,
      ...(onToggle ? { 'aria-pressed': selected } : {}),
    }
    : {}

  if (!src || failed) {
    return (
      <Wrapper
        {...wrapperProps}
        className={cn(
          'grid aspect-[3/4] w-full place-items-center rounded-lg border border-rule bg-paper-2 text-ink-soft',
          selected && 'ring-2 ring-gold'
        )}
      >
        <ImageOff className="h-5 w-5" />
        <span className="sr-only">{t('common.imageUnavailable')}</span>
      </Wrapper>
    )
  }

  const crop = zoom && dims && bbox ? computeCrop(bbox, dims.w, dims.h) : null

  // Face outline, positioned relative to whatever region is on screen. Only the
  // frame carries those coordinates — on the crop fallback the image IS the face.
  const box = detection.frameUrl && bbox && dims
    ? (crop
      ? {
        left: ((bbox.x - crop.x) / crop.w) * 100,
        top: ((bbox.y - crop.y) / crop.h) * 100,
        width: (bbox.width / crop.w) * 100,
        height: (bbox.height / crop.h) * 100,
      }
      : {
        left: (bbox.x / dims.w) * 100,
        top: (bbox.y / dims.h) * 100,
        width: (bbox.width / dims.w) * 100,
        height: (bbox.height / dims.h) * 100,
      })
    : null

  return (
    <Wrapper
      {...wrapperProps}
      className={cn(
        'relative block w-full overflow-hidden rounded-lg border border-rule bg-ink',
        action && 'transition focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        onClick && !onToggle && 'hover:ring-2 hover:ring-gold',
        selected && 'ring-2 ring-gold ring-offset-1 ring-offset-paper'
      )}
      style={{
        aspectRatio: crop
          ? `${crop.w} / ${crop.h}`
          : dims
            ? `${dims.w} / ${dims.h}`
            : '3 / 4',
      }}
    >
      <img
        src={src}
        alt=""
        onLoad={(e) => setDims({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
        onError={() => setFailed(true)}
        className={cn('block', crop ? 'absolute max-w-none' : 'h-full w-full object-cover')}
        style={crop && dims
          ? {
            width: `${(dims.w / crop.w) * 100}%`,
            height: `${(dims.h / crop.h) * 100}%`,
            left: `${(-crop.x / crop.w) * 100}%`,
            top: `${(-crop.y / crop.h) * 100}%`,
          }
          : undefined}
      />
      {box && (
        <div
          className="pointer-events-none absolute box-border rounded-[2px] border-2 border-gold"
          style={{
            left: `${box.left}%`,
            top: `${box.top}%`,
            width: `${box.width}%`,
            height: `${box.height}%`,
            boxShadow: '0 0 0 1px rgba(0,0,0,0.35)',
          }}
        />
      )}
      {badge && (
        <span className="absolute left-1 top-1 rounded-sm bg-ink/80 px-1 py-0.5 text-[10px] font-medium leading-none text-paper">
          {badge}
        </span>
      )}
      {onToggle && (
        <span
          className={cn(
            'absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-sm border',
            selected
              ? 'border-gold bg-gold text-ink'
              : 'border-white/70 bg-ink/40 text-transparent'
          )}
        >
          <Check className="h-3.5 w-3.5" />
        </span>
      )}
      {detection.timestamp && (
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent px-1.5 pb-1 pt-4 text-left text-[10px] leading-tight text-white">
          <div className="truncate">{formatDateTimeCompact(detection.timestamp)}</div>
          {detection.cameraName && (
            <div className="flex items-center gap-1 truncate opacity-80">
              <Camera className="h-2.5 w-2.5 shrink-0" />
              <span className="truncate">{detection.cameraName}</span>
            </div>
          )}
        </div>
      )}
      {detection.bodyMatch != null && (
        // A LOW score is the signal worth acting on: this person's body matches
        // none of their other appearances. Highlighted accordingly.
        <span
          title={t('mergeSuggestions.bodyMatchHint', {
            gap: formatGap(detection.bodyMatchGapMinutes ?? null),
          })}
          className={cn(
            'absolute bottom-1 right-1 inline-flex items-center gap-0.5 rounded-sm px-1 py-0.5 text-[10px] font-medium leading-none',
            detection.bodyMatch < BODY_WEAK_MATCH
              ? 'bg-[#7a3325] text-white'
              : 'bg-black/70 text-white/90'
          )}
        >
          <Shirt className="h-2.5 w-2.5" />
          {Math.round(detection.bodyMatch * 100)}%
        </span>
      )}
    </Wrapper>
  )
}
